import { Cart } from '../types';

/**
 * One request against the storefront cart api, and the cart it answered with.
 *
 * Every write endpoint under `/services_api/storefront/v1/cart` returns the whole cart,
 * serialized with the same groups, whatever the operation was — so a caller never has to
 * follow a change with a read, and every operation in this folder can share this.
 *
 * Failures carry a reason. `serializeFormErrorsToJson` puts the action's own message
 * under `errors`, which is how `OUTOFSTOCK` reaches the customer as something other than
 * "400".
 */
export async function cartRequest(url: string, init: RequestInit = {}): Promise<Cart> {
  const response = await fetch(url, {
    credentials: 'same-origin',
    ...init,
    headers: { 'Content-Type': 'application/json', ...(init.headers || {}) },
  });

  if (!response.ok) {
    throw new Error(await failureMessage(response));
  }

  return (await response.json()) as Cart;
}

export async function failureMessage(response: Response): Promise<string> {
  try {
    const body = await response.json();
    const reason = Array.isArray(body?.errors) ? body.errors[0] : body?.message;

    if (reason) {
      return String(reason);
    }
  } catch (error) {
    // Not JSON, or empty. The status is all there is.
  }

  return `The cart request failed with ${response.status}`;
}
